(() => {
  const headerSelector = '[data-weylan-header]';
  const dialogSelector = '[data-weylan-dialog]';

  const debounce = (callback, wait) => {
    let timer;

    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), wait);
    };
  };

  const getHeader = () => document.querySelector(headerSelector);

  const syncDialogState = () => {
    const hasOpenDialog = Boolean(document.querySelector(`${dialogSelector}[open]`));
    document.documentElement.classList.toggle('weylan-dialog-open', hasOpenDialog);
  };

  const setDialogTriggerState = (root, name, expanded) => {
    root.querySelectorAll(`[data-open-dialog="${name}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  };

  const closeDialog = (dialog) => {
    if (dialog?.open) dialog.close();
  };

  const closeOtherDialogs = (root, currentDialog) => {
    root.querySelectorAll(`${dialogSelector}[open]`).forEach((dialog) => {
      if (dialog === currentDialog) return;
      dialog.weylanSkipFocusRestore = true;
      dialog.close();
    });
  };

  const isLegacyDisclosure = (item) => item?.matches('details');

  const isDesktopMenuOpen = (item) =>
    Boolean(item && (isLegacyDisclosure(item) ? item.open : item.classList.contains('is-open')));

  const syncMenuOverlay = (root) => {
    const overlay = root.querySelector('[data-menu-overlay]');
    if (!overlay) return;

    overlay.hidden = !Array.from(root.querySelectorAll('[data-desktop-menu]')).some(isDesktopMenuOpen);
  };

  const setDesktopMenuState = (root, item, expanded) => {
    if (!item) return;

    if (isLegacyDisclosure(item)) {
      item.open = expanded;
      syncMenuOverlay(root);
      return;
    }

    const toggle = item.querySelector('[data-desktop-menu-toggle]');
    const panel = item.querySelector('[data-desktop-menu-panel]');
    item.classList.toggle('is-open', expanded);
    if (panel) panel.hidden = !expanded;
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.setAttribute(
        'aria-label',
        expanded ? toggle.dataset.closeLabel || '' : toggle.dataset.openLabel || ''
      );
    }
    syncMenuOverlay(root);
  };

  const closeDesktopMenus = (root, current = null) => {
    root.querySelectorAll('[data-desktop-menu]').forEach((item) => {
      if (item !== current && isDesktopMenuOpen(item)) setDesktopMenuState(root, item, false);
    });
    syncMenuOverlay(root);
  };

  const isMobileMenuOpen = (item) =>
    Boolean(item && (isLegacyDisclosure(item) ? item.open : item.classList.contains('is-open')));

  const setMobileMenuState = (item, expanded) => {
    if (!item) return;
    if (isLegacyDisclosure(item)) {
      item.open = expanded;
      return;
    }

    const toggle = item.querySelector('[data-mobile-menu-toggle]');
    const panel = item.querySelector('[data-mobile-menu-panel]');
    item.classList.toggle('is-open', expanded);
    if (panel) panel.hidden = !expanded;
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.setAttribute(
        'aria-label',
        expanded ? toggle.dataset.closeLabel || '' : toggle.dataset.openLabel || ''
      );
    }
  };

  const closeMobileMenus = (root, current = null) => {
    root.querySelectorAll('[data-mobile-menu]').forEach((item) => {
      if (item !== current && isMobileMenuOpen(item)) setMobileMenuState(item, false);
    });
  };

  const openDialog = (root, name, trigger = null) => {
    const dialog = root.querySelector(`[data-weylan-dialog="${name}"]`);
    if (!dialog) return;

    const sourceDialog = trigger?.closest(dialogSelector);
    dialog.weylanReturnFocus =
      sourceDialog?.weylanReturnFocus ||
      trigger ||
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);

    closeDesktopMenus(root);
    closeOtherDialogs(root, dialog);

    if (!dialog.open) dialog.showModal();
    setDialogTriggerState(root, name, true);
    syncDialogState();

    window.requestAnimationFrame(() => {
      const initialFocus =
        name === 'search'
          ? dialog.querySelector('[data-predictive-input]')
          : dialog.querySelector(
              '[data-close-dialog], a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
            );
      initialFocus?.focus();
    });
  };

  const updateCartBadge = (root, freshRoot, itemCount) => {
    const currentBadge = root.querySelector('[data-cart-count]');
    const freshBadge = freshRoot?.querySelector('[data-cart-count]');
    if (!currentBadge) return;

    if (freshBadge) {
      currentBadge.textContent = freshBadge.textContent;
      currentBadge.hidden = freshBadge.hidden;
      return;
    }

    if (Number.isFinite(itemCount)) {
      currentBadge.textContent = String(itemCount);
      currentBadge.hidden = itemCount < 1;
    }
  };

  const updateHeaderFromMarkup = (root, markup, itemCount) => {
    if (!markup) {
      updateCartBadge(root, null, itemCount);
      return false;
    }

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const freshRoot = parsed.querySelector(headerSelector);
    if (!freshRoot) {
      updateCartBadge(root, null, itemCount);
      return false;
    }

    const currentDrawer = root.querySelector('[data-cart-drawer-content]');
    const freshDrawer = freshRoot.querySelector('[data-cart-drawer-content]');

    if (currentDrawer && freshDrawer) currentDrawer.replaceWith(freshDrawer);
    updateCartBadge(root, freshRoot, itemCount);
    return true;
  };

  const refreshHeader = async (root) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', root.dataset.sectionId);

    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(root.dataset.cartError);

    const sections = await response.json();
    updateHeaderFromMarkup(root, sections[root.dataset.sectionId]);
  };

  const applyCartResponse = async (root, data) => {
    const markup = data.sections?.[root.dataset.sectionId];
    const updated = updateHeaderFromMarkup(root, markup, Number(data.item_count));
    if (!updated) await refreshHeader(root);

    document.dispatchEvent(
      new CustomEvent('weylan:cart-updated', {
        detail: { itemCount: Number(data.item_count) || null },
      })
    );
  };

  const setCartStatus = (root, message, tone = '') => {
    const status = root.querySelector('[data-cart-status]');
    if (!status) return;

    status.textContent = message || '';
    if (tone) {
      status.dataset.statusTone = tone;
    } else {
      delete status.dataset.statusTone;
    }
  };

  const setCartControlsDisabled = (content, disabled) => {
    content?.querySelectorAll('[data-cart-line-key][data-cart-quantity], .weylan-cart-drawer__actions button').forEach((control) => {
      if (disabled) {
        control.dataset.cartWasDisabled = String(control.disabled);
        control.disabled = true;
        return;
      }

      control.disabled = control.dataset.cartWasDisabled === 'true';
      delete control.dataset.cartWasDisabled;
    });
  };

  const focusAfterCartChange = (root, responseData, options) => {
    const fallback = root.querySelector('[data-cart-focus-fallback]');
    const items = Array.from(root.querySelectorAll('[data-cart-item]'));
    const requestedIndex = Math.max(0, options.line - 1);

    if (options.action === 'remove') {
      const adjacentItem = items[Math.min(requestedIndex, Math.max(0, items.length - 1))];
      const adjacentTarget = adjacentItem?.querySelector('button:not([disabled]), a[href]');
      (adjacentTarget || fallback)?.focus({ preventScroll: true });
      return;
    }

    const responseKey = responseData.items?.[requestedIndex]?.key || options.itemKey;
    const matchingItem = items.find((item) => item.dataset.cartLineKey === String(responseKey)) || items[requestedIndex];
    const matchingControl = Array.from(matchingItem?.querySelectorAll('[data-cart-action]') || []).find(
      (control) => control.dataset.cartAction === options.controlAction && !control.disabled
    );
    const fallbackControl = matchingItem?.querySelector('button:not([disabled]), a[href]');
    (matchingControl || fallbackControl || fallback)?.focus({ preventScroll: true });
  };

  const changeCartLine = async (root, trigger) => {
    const content = root.querySelector('[data-cart-drawer-content]');
    if (content?.getAttribute('aria-busy') === 'true') return;

    const itemKey = trigger.dataset.cartLineKey;
    const line = Number(trigger.dataset.cartLine);
    const quantity = Math.max(0, Number(trigger.dataset.cartQuantity));
    const action = quantity === 0 ? 'remove' : 'update';
    const successMessage = trigger.dataset.cartSuccess || (action === 'remove' ? root.dataset.cartRemoved : root.dataset.cartUpdated);

    content?.setAttribute('aria-busy', 'true');
    setCartControlsDisabled(content, true);
    setCartStatus(root, root.dataset.cartUpdating, 'progress');

    try {
      const payload = {
        quantity,
        sections: root.dataset.sectionId,
        sections_url: `${window.location.pathname}${window.location.search}`,
      };
      if (itemKey) {
        payload.id = itemKey;
      } else {
        payload.line = line;
      }

      const response = await fetch(root.dataset.cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || root.dataset.cartError);

      await applyCartResponse(root, data);
      setCartStatus(root, successMessage, 'success');
      window.requestAnimationFrame(() => {
        focusAfterCartChange(root, data, {
          action,
          controlAction: trigger.dataset.cartAction,
          itemKey,
          line,
        });
      });
    } catch (error) {
      content?.setAttribute('aria-busy', 'false');
      setCartControlsDisabled(content, false);
      setCartStatus(root, error.message || root.dataset.cartError, 'error');
      trigger.focus({ preventScroll: true });
    }
  };

  const initPredictiveSearch = (root) => {
    const input = root.querySelector('[data-predictive-input]');
    const results = root.querySelector('[data-predictive-results]');
    const status = root.querySelector('[data-search-status]');
    const clearButton = root.querySelector('[data-search-clear]');
    if (!input || !results || !status || !clearButton) return;

    let requestController;

    const clearResults = () => {
      requestController?.abort();
      results.replaceChildren();
      status.textContent = '';
      input.setAttribute('aria-expanded', 'false');
      clearButton.hidden = input.value.length === 0;
    };

    const requestResults = debounce(async () => {
      const term = input.value.trim();
      clearButton.hidden = input.value.length === 0;

      if (term.length < 2) {
        clearResults();
        return;
      }

      requestController?.abort();
      requestController = new AbortController();
      status.textContent = root.dataset.searchLoading;

      try {
        const url = new URL(root.dataset.predictiveSearchUrl, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '8');
        url.searchParams.set('section_id', 'predictive-search');

        const response = await fetch(url, {
          signal: requestController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(String(response.status));

        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        results.innerHTML = section?.innerHTML || '';
        input.setAttribute('aria-expanded', String(Boolean(results.textContent.trim())));
        status.textContent = results.textContent.trim() ? '' : root.dataset.searchEmpty;
      } catch (error) {
        if (error.name === 'AbortError') return;
        results.replaceChildren();
        input.setAttribute('aria-expanded', 'false');
        status.textContent = root.dataset.searchError;
      }
    }, 250);

    input.addEventListener('input', requestResults);
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown') return;
      const firstResult = results.querySelector('a, button');
      if (!firstResult) return;
      event.preventDefault();
      firstResult.focus();
    });

    results.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const options = Array.from(results.querySelectorAll('a, button'));
      const currentIndex = options.indexOf(document.activeElement);
      if (currentIndex < 0) return;

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = (currentIndex + direction + options.length) % options.length;
      options[nextIndex].focus();
    });

    clearButton.addEventListener('click', () => {
      input.value = '';
      clearResults();
      input.focus();
    });

    clearResults();
  };

  const initHeader = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    root.addEventListener('click', (event) => {
      const desktopToggle = event.target.closest('[data-desktop-menu-toggle]');
      if (desktopToggle && root.contains(desktopToggle)) {
        event.preventDefault();
        const item = desktopToggle.closest('[data-desktop-menu]');
        const shouldOpen = !isDesktopMenuOpen(item);
        closeDesktopMenus(root, shouldOpen ? item : null);
        setDesktopMenuState(root, item, shouldOpen);
        return;
      }

      const desktopSummary = event.target.closest('[data-desktop-menu] summary');
      if (desktopSummary && root.contains(desktopSummary)) {
        const details = desktopSummary.closest('[data-desktop-menu]');
        if (!details.open) closeDesktopMenus(root, details);
      }

      const mobileToggle = event.target.closest('[data-mobile-menu-toggle]');
      if (mobileToggle && root.contains(mobileToggle)) {
        event.preventDefault();
        const item = mobileToggle.closest('[data-mobile-menu]');
        const shouldOpen = !isMobileMenuOpen(item);
        closeMobileMenus(root, shouldOpen ? item : null);
        setMobileMenuState(item, shouldOpen);
        return;
      }

      const mobileBack = event.target.closest('[data-mobile-menu-back]');
      if (mobileBack && root.contains(mobileBack)) {
        event.preventDefault();
        const item = mobileBack.closest('[data-mobile-menu]');
        const toggle = item?.querySelector('[data-mobile-menu-toggle], summary');
        setMobileMenuState(item, false);
        toggle?.focus();
        return;
      }

      const overlay = event.target.closest('[data-menu-overlay]');
      if (overlay && root.contains(overlay)) {
        closeDesktopMenus(root);
        return;
      }

      const openTrigger = event.target.closest('[data-open-dialog]');
      if (openTrigger && root.contains(openTrigger)) {
        event.preventDefault();
        openDialog(root, openTrigger.dataset.openDialog, openTrigger);
        return;
      }

      const closeTrigger = event.target.closest('[data-close-dialog]');
      if (closeTrigger && root.contains(closeTrigger)) {
        closeDialog(closeTrigger.closest(dialogSelector));
        return;
      }

      const quantityTrigger = event.target.closest('[data-cart-line][data-cart-quantity]');
      if (quantityTrigger && root.contains(quantityTrigger)) {
        event.preventDefault();
        changeCartLine(root, quantityTrigger);
      }
    });

    root.querySelectorAll(dialogSelector).forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });

      dialog.addEventListener('close', () => {
        setDialogTriggerState(root, dialog.dataset.weylanDialog, false);
        if (dialog.dataset.weylanDialog === 'menu') closeMobileMenus(root);
        syncDialogState();

        const returnFocus = dialog.weylanReturnFocus;
        const shouldRestoreFocus = !dialog.weylanSkipFocusRestore;
        dialog.weylanReturnFocus = null;
        dialog.weylanSkipFocusRestore = false;

        if (shouldRestoreFocus && returnFocus?.isConnected) {
          window.requestAnimationFrame(() => returnFocus.focus());
        }
      });
    });

    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const menuOpenDelay = 320;
    const menuCloseDelay = 350;
    let menuOpenTimer;
    let menuCloseTimer;

    const cancelMenuOpen = () => {
      window.clearTimeout(menuOpenTimer);
    };

    const cancelMenuClose = () => {
      window.clearTimeout(menuCloseTimer);
    };

    const openDesktopMenu = (item) => {
      cancelMenuOpen();
      cancelMenuClose();
      closeDesktopMenus(root, item);
      setDesktopMenuState(root, item, true);
    };

    const scheduleDesktopMenuOpen = (item) => {
      cancelMenuOpen();
      cancelMenuClose();
      menuOpenTimer = window.setTimeout(() => openDesktopMenu(item), menuOpenDelay);
    };

    const scheduleDesktopMenuClose = (item) => {
      cancelMenuOpen();
      cancelMenuClose();
      menuCloseTimer = window.setTimeout(() => {
        setDesktopMenuState(root, item, false);
      }, menuCloseDelay);
    };

    root.querySelectorAll('[data-desktop-menu]').forEach((item) => {
      const disclosure = item.querySelector('[data-desktop-menu-toggle], summary');

      if (isLegacyDisclosure(item)) {
        item.addEventListener('toggle', () => {
          if (item.open) closeDesktopMenus(root, item);
          syncMenuOverlay(root);
        });
      }

      item.addEventListener('pointerenter', () => {
        if (!finePointer.matches) return;
        cancelMenuClose();
        if (!isDesktopMenuOpen(item)) scheduleDesktopMenuOpen(item);
      });

      item.addEventListener('pointerleave', () => {
        if (finePointer.matches) scheduleDesktopMenuClose(item);
      });

      item.addEventListener('focusin', () => {
        cancelMenuOpen();
        cancelMenuClose();
      });
      item.addEventListener('focusout', (event) => {
        if (isDesktopMenuOpen(item) && !item.contains(event.relatedTarget)) {
          scheduleDesktopMenuClose(item);
        }
      });

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !isDesktopMenuOpen(item)) return;
        event.preventDefault();
        cancelMenuOpen();
        cancelMenuClose();
        setDesktopMenuState(root, item, false);
        disclosure?.focus();
      });
    });

    root.querySelectorAll('[data-mobile-menu]').forEach((item) => {
      if (isLegacyDisclosure(item)) {
        item.addEventListener('toggle', () => {
          if (item.open) closeMobileMenus(root, item);
        });
      } else {
        setMobileMenuState(item, item.classList.contains('is-open'));
      }

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !isMobileMenuOpen(item)) return;
        event.preventDefault();
        setMobileMenuState(item, false);
        item.querySelector('[data-mobile-menu-toggle], summary')?.focus();
      });
    });

    const handlePointerModeChange = (event) => {
      if (!event.matches) closeDesktopMenus(root);
    };
    if (typeof finePointer.addEventListener === 'function') {
      finePointer.addEventListener('change', handlePointerModeChange);
    } else {
      finePointer.addListener(handlePointerModeChange);
    }

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-desktop-menu]')) closeDesktopMenus(root);
    });

    syncMenuOverlay(root);
    initPredictiveSearch(root);
  };

  const setProductButtons = (productRoot, text, disabled) => {
    productRoot?.querySelectorAll('[data-add-to-cart], [data-mobile-add]').forEach((button) => {
      button.disabled = disabled;
      button.textContent = text;
    });
  };

  const restoreProductButtons = (productRoot) => {
    const availability = productRoot?.dataset.availability;
    if (availability === 'available_to_order') {
      setProductButtons(productRoot, productRoot.dataset.addText, false);
    } else if (availability === 'not_orderable') {
      setProductButtons(productRoot, productRoot.dataset.notOrderableText, true);
    } else {
      setProductButtons(productRoot, productRoot.dataset.unavailableText, true);
    }
  };

  const initProductFormBridge = () => {
    if (window.weylanProductFormBridgeInitialized) return;
    window.weylanProductFormBridgeInitialized = true;

    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('form.weylan-product__form');
      const submitter = event.submitter;
      const isStandardAdd =
        submitter?.matches('[data-add-to-cart], [data-mobile-add]') ||
        submitter?.getAttribute('name') === 'add';

      if (!form || !isStandardAdd) return;

      const root = getHeader();
      const productRoot = form.closest('[data-weylan-product]');
      if (!root || !productRoot || form.dataset.cartBusy === 'true') return;

      event.preventDefault();
      form.dataset.cartBusy = 'true';
      form.setAttribute('aria-busy', 'true');

      const status = productRoot.querySelector('[data-product-form-message]');
      setProductButtons(productRoot, root.dataset.cartAdding, true);
      if (status) status.textContent = root.dataset.cartAdding;

      try {
        const formData = new FormData(form);
        formData.set('sections', root.dataset.sectionId);
        formData.set('sections_url', `${window.location.pathname}${window.location.search}`);

        const response = await fetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || root.dataset.cartError);

        await applyCartResponse(root, data);
        const successMessage = productRoot.dataset.cartAddedMessage || root.dataset.cartAdded;
        setProductButtons(productRoot, root.dataset.cartAdded, true);
        if (status) status.textContent = successMessage;
        setCartStatus(root, successMessage, 'success');
        openDialog(root, 'cart');

        window.setTimeout(() => {
          restoreProductButtons(productRoot);
        }, 1600);
      } catch (error) {
        restoreProductButtons(productRoot);
        if (status) {
          status.textContent = error.message || root.dataset.cartError;
          status.focus({ preventScroll: true });
        }
      } finally {
        form.dataset.cartBusy = 'false';
        form.setAttribute('aria-busy', 'false');
      }
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll(headerSelector).forEach(initHeader);
    initProductFormBridge();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
